import React, { ComponentType, FunctionComponent } from "react";
import { I18n } from "@lingui/core";
import { TransRenderProps, useLingui } from "@lingui/react";

export type I18nContext = {
  i18n: I18n;
  defaultComponent?: ComponentType<TransRenderProps>;
};

export type withI18nProps = {
  i18n: I18n;
};

export type I18nProviderProps = I18nContext & {
  forceRenderOnLocaleChange?: boolean;
  children?: React.ReactNode;
};

const LinguiContext = React.createContext<I18nContext | null>(null);

export function withI18n(
  o?: object
): <P extends withI18nProps>(
  Component: ComponentType<P>
) => React.ComponentType<Omit<P, "i18n">> {
  return <P extends withI18nProps>(
    WrappedComponent: ComponentType<P>
  ): ComponentType<Omit<P, "i18n">> => {
    return (props: Omit<P, "i18n">) => {
      if (process.env.NODE_ENV !== "production") {
        if (typeof o === "function" || React.isValidElement(o)) {
          throw new Error(
            "withI18n([options]) takes options as a first argument, " +
              "but received React component itself. Without options, the Component " +
              "should be wrapped as withI18n()(Component), not withI18n(Component)."
          );
        }
      }
      const { i18n } = useLingui();
      return <WrappedComponent {...(props as P)} i18n={i18n} />;
    };
  };
}
